import { TrafficInfoFilterItem, TrafficInfoResponse } from './traffic-info-types';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { ReactComponent as RailwayIcon } from '../../assets/icons/icon-railway.svg';
import { ReactComponent as BusIcon } from '../../assets/icons/icon-bus.svg';
import styles from './components/traffic-info-filter.module.scss';
import {
  filterListByLineNumber,
  filterListByTimeInterval as filterListByDate,
  filterListByType,
  sortListByFromDate,
} from './traffic-info-utils';

export interface TrafficInfoContextData {
  allLines: TrafficInfoResponse['infos'];
  filteredLines: TrafficInfoResponse['infos'];
  typeFilters: TrafficInfoFilterItem[];
  setTypeFilters: (filters: TrafficInfoFilterItem[]) => void;
  toggleTypeFilter: (filter: TrafficInfoFilterItem) => void;
  lineNumberFilter: string | undefined;
  setLineNumberFilter: (lineNumberFilter: string | undefined) => void;
  dateFilter: string | undefined;
  setDateFilter: (filter: string | undefined) => void;
}

const TrafficInfoContext = createContext<TrafficInfoContextData>({
  allLines: [],
  filteredLines: [],
  typeFilters: [],
  setTypeFilters: () => undefined,
  toggleTypeFilter: () => undefined,
  lineNumberFilter: undefined,
  setLineNumberFilter: () => undefined,
  dateFilter: undefined,
  setDateFilter: () => undefined,
});

export interface TrafficInfoContextProviderProps {
  lines: TrafficInfoResponse['infos'];
  children: React.ReactNode;
}

export const TrafficInfoContextProvider = ({ lines, children }: TrafficInfoContextProviderProps): JSX.Element => {
  const [filteredLines, setFilteredLines] = useState<TrafficInfoResponse['infos']>([]);
  const [typeFilters, setTypeFilters] = useState<TrafficInfoFilterItem[]>([
    {
      label: 'U-/Stadtbahn',
      icon: <RailwayIcon />,
      type: 'subway',
      active: false,
      className: styles.TrafficInfoFilterItemBlue,
    },
    {
      label: 'Straßenbahn',
      icon: <RailwayIcon />,
      type: 'tram',
      active: false,
      className: styles.TrafficInfoFilterItemRed,
    },
    { label: 'Bus', icon: <BusIcon />, type: 'bus', active: false, className: styles.TrafficInfoFilterItemYellow },
  ]);

  const [lineNumberFilter, setLineNumberFilter] = useState<string | undefined>(undefined);
  const [dateFilter, setDateFilter] = useState<string | undefined>(undefined);

  const toggleTypeFilter = useCallback((filterToUpdate: TrafficInfoFilterItem) => {
    setTypeFilters((prevFilters) => {
      return prevFilters.map((currentFilter) => {
        if (currentFilter.type === filterToUpdate.type) {
          currentFilter.active = !currentFilter.active;
        }
        return currentFilter;
      });
    });
  }, []);

  useEffect(() => {
    let newLines = filterListByType(lines, typeFilters);
    newLines = filterListByLineNumber(newLines, lineNumberFilter);
    newLines = filterListByDate(newLines, dateFilter);
    newLines = sortListByFromDate(newLines);
    setFilteredLines(newLines);
  }, [lineNumberFilter, lines, dateFilter, typeFilters]);

  return (
    <TrafficInfoContext.Provider
      value={{
        allLines: lines,
        filteredLines,
        typeFilters,
        setTypeFilters,
        toggleTypeFilter,
        lineNumberFilter,
        setLineNumberFilter,
        dateFilter,
        setDateFilter,
      }}
    >
      {children}
    </TrafficInfoContext.Provider>
  );
};

export function useTrafficInfoContext(): TrafficInfoContextData {
  return useContext(TrafficInfoContext);
}
